import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Receipt, FileText, AlertCircle, DollarSign } from 'lucide-react';
import { GET_REPORTE_RESUMEN_TRIBUTARIO } from '../../graphql/reportes';
import FiltrosReporte from './FiltrosReporte';
import Loading from '../shared/Loading';
import Button from '../shared/Button';
import './ReporteResumenTributario.css';

function ReporteResumenTributario() {
  const [datos, setDatos] = useState(null);
  const [getReporte, { loading, error }] = useLazyQuery(GET_REPORTE_RESUMEN_TRIBUTARIO, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setDatos(data.reporteResumenTributario),
    onError: (err) => console.error('Error al obtener Resumen Tributario:', err)
  });

  const handleFiltrar = ({ fechaDesde, fechaHasta }) => {
    getReporte({ variables: { fechaDesde, fechaHasta } });
  };

  const formatearMoneda = (valor) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);

  const formatearPorcentaje = (valor) => `${parseFloat(valor || 0).toFixed(2)}%`;

  const formatMes = (mes) => {
    const [anio, m] = mes.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(m) - 1]} ${anio}`;
  };

  return (
    <div className="reporte-resumen-tributario">
      <FiltrosReporte onFiltrar={handleFiltrar} loading={loading} />

      {loading && <Loading />}
      {error && <div className="error-message">Error: {error.message}</div>}

      {datos && (
        <>
          {/* Cards resumen IVA */}
          <div className="reporte-cards">
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--success">
                <DollarSign size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">IVA Generado</div>
                <div className="reporte-card__value">{formatearMoneda(datos.iva_generado)}</div>
                <div className="reporte-card__extra">Cobrado en ventas</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--danger">
                <Receipt size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">IVA Pagado</div>
                <div className="reporte-card__value">{formatearMoneda(datos.iva_pagado_gastos + datos.iva_pagado_compras)}</div>
                <div className="reporte-card__extra">Gastos: {formatearMoneda(datos.iva_pagado_gastos)} | Compras: {formatearMoneda(datos.iva_pagado_compras)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className={`reporte-card__icon reporte-card__icon--${datos.iva_neto >= 0 ? 'warning' : 'success'}`}>
                <AlertCircle size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">IVA Neto (a pagar DIAN)</div>
                <div className="reporte-card__value">{formatearMoneda(datos.iva_neto)}</div>
                <div className="reporte-card__extra">{datos.iva_neto > 0 ? 'A favor de la DIAN' : 'Saldo a favor'}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--info">
                <FileText size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Facturas Electrónicas</div>
                <div className="reporte-card__value">{datos.total_facturas_electronicas}</div>
                <div className="reporte-card__extra">
                  {datos.facturas_pendientes_envio > 0
                    ? `${datos.facturas_pendientes_envio} pendientes de envío`
                    : 'Todas enviadas'}
                </div>
              </div>
            </div>
          </div>

          {/* IVA Generado */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">IVA Generado (Ventas)</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Base Gravable</th>
                    <th>Tarifa IVA</th>
                    <th>Valor IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.desglose_iva_generado.map((item, i) => (
                    <tr key={i}>
                      <td><strong>{item.concepto}</strong></td>
                      <td>{formatearMoneda(item.base_gravable)}</td>
                      <td>{formatearPorcentaje(item.tarifa)}</td>
                      <td>{formatearMoneda(item.valor)}</td>
                    </tr>
                  ))}
                  <tr className="fila-total">
                    <td colSpan={3}><strong>TOTAL IVA GENERADO</strong></td>
                    <td><strong>{formatearMoneda(datos.iva_generado)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* IVA Pagado */}
          {datos.desglose_iva_pagado.length > 0 && (
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">IVA Pagado (Gastos y Compras)</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Concepto</th>
                      <th>Base Gravable</th>
                      <th>Tarifa IVA</th>
                      <th>Valor IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.desglose_iva_pagado.map((item, i) => (
                      <tr key={i}>
                        <td>{item.concepto}</td>
                        <td>{formatearMoneda(item.base_gravable)}</td>
                        <td>{formatearPorcentaje(item.tarifa)}</td>
                        <td>{formatearMoneda(item.valor)}</td>
                      </tr>
                    ))}
                    <tr className="fila-total">
                      <td colSpan={3}><strong>TOTAL IVA PAGADO</strong></td>
                      <td><strong>{formatearMoneda(datos.iva_pagado_gastos + datos.iva_pagado_compras)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Resumen Mensual */}
          {datos.resumen_mensual.length > 0 && (
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">Resumen Tributario Mensual</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th>Ingresos Gravados</th>
                      <th>Ingresos Exentos</th>
                      <th>IVA Generado</th>
                      <th>IVA Pagado</th>
                      <th>IVA Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.resumen_mensual.map(mes => (
                      <tr key={mes.mes}>
                        <td><strong>{formatMes(mes.mes)}</strong></td>
                        <td>{formatearMoneda(mes.ingresos_gravados)}</td>
                        <td>{formatearMoneda(mes.ingresos_exentos)}</td>
                        <td className="text-success">{formatearMoneda(mes.iva_generado)}</td>
                        <td className="text-danger">{formatearMoneda(mes.iva_pagado)}</td>
                        <td className={mes.iva_neto >= 0 ? 'text-warning' : 'text-success'}>
                          <strong>{formatearMoneda(mes.iva_neto)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Facturación electrónica extra */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Facturación Electrónica</h3>
            <div className="fe-stats">
              <div className="fe-stat">
                <span className="fe-stat__label">Total Emitidas</span>
                <span className="fe-stat__value">{datos.total_facturas_electronicas}</span>
              </div>
              <div className="fe-stat">
                <span className="fe-stat__label">Total Facturado</span>
                <span className="fe-stat__value">{formatearMoneda(datos.total_facturado_electronicamente)}</span>
              </div>
              <div className="fe-stat">
                <span className="fe-stat__label">Facturas Totales</span>
                <span className="fe-stat__value">{datos.num_facturas}</span>
              </div>
              <div className="fe-stat">
                <span className="fe-stat__label">Notas Crédito</span>
                <span className="fe-stat__value">{datos.num_notas_credito}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {!datos && !loading && (
        <div className="reporte-placeholder">
          <Receipt size={64} />
          <p>Selecciona un rango de fechas para generar el Resumen Tributario</p>
        </div>
      )}
    </div>
  );
}

export default ReporteResumenTributario;
